import { Plus } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Steps, Callout, RoleBadge, pathFor,
  Diagram, MockField, MockSelect, MockButton, MockRow,
} from "../shared";

export default function PagesRegisterSection() {
  return (
    <div data-testid="guide-pages-register">
      <PageHeading
        icon={Plus}
        title="Register"
        description="The form for creating one new action item. Use it for one-off items that didn't come from a meeting (items from a meeting flow in through Meeting Tasks instead)."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="admin" />
        <span className="text-xs text-white/40">General Admin and above can create items.</span>
      </div>

      <Callout variant="info" title="In plain words">
        This page is simply a <span className="text-white">blank form</span>. You fill in what the item is, who it's for, and how it should be handled — then save. The system then opens the brand-new item so you can start working on it straight away.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-xs text-white/55 space-y-1.5 ml-2">
          <li>When a single task pops up that isn't tied to a meeting.</li>
          <li>When you need to log something quickly and assign it to a person.</li>
          <li>For meeting-driven tasks, use <Link href={pathFor("pages/meeting-tasks")} className="text-cyan-300 hover:underline">Meeting Tasks</Link> instead — it can register many at once.</li>
        </ul>
      </Section>

      <Section title="A picture of the page">
        <Diagram
          frameLabel="Register"
          legend={[
            <>The <span className="text-white">left column</span> is for the content — what the item is and the instruction.</>,
            <>The <span className="text-white">right column</span> is for the governance settings — lane, priority, sign-off, and review.</>,
            <>If a required box is empty it turns <span className="text-red-300">red</span> and the Save button stays switched off until you fix it.</>,
            <>Press <span className="text-white">Save</span> to create the item and open its detail page.</>,
          ]}
        >
          <MockRow>
            <div className="flex-1 space-y-2 min-w-[180px]">
              <MockField label="Title" placeholder="Short name for the item…" marker={1} />
              <MockField label="Instruction" placeholder="What needs to be done…" />
            </div>
            <div className="flex-1 space-y-2 min-w-[160px]">
              <MockSelect label="Lane" value="SDF MD" marker={2} />
              <MockSelect label="Priority" value="High" />
            </div>
          </MockRow>
          <MockField label="Due date" placeholder="Required" marker={3} />
          <MockButton label="Save" marker={4} />
        </Diagram>
        <Steps
          steps={[
            <>Fill in the <span className="text-white">content</span> on the left — title and instruction.</>,
            <>Set the <span className="text-white">governance settings</span> on the right — lane, priority, whether sign-off and review are needed.</>,
            <>If anything turns <span className="text-red-300">red</span>, fill it in — the Save button only switches on once all required boxes are complete.</>,
            <>Press <span className="text-white">Save</span>. You're taken to the new item, where you can set it to In Progress, hand it off, or add sub-tasks.</>,
          ]}
        />
      </Section>

      <Callout variant="tip">
        For a full field-by-field walkthrough — what each box means and what happens after you save — see <Link href={pathFor("forms/register")} className="text-cyan-300 hover:underline" data-testid="link-walkthrough-register">Register an Action Item</Link>.
      </Callout>
    </div>
  );
}
